/**
 * MirrorBloom.tsx — Pattern Glow™ wrapper page
 * Route: /mirror-bloom
 * Visual: Cream bg, rose-brown accent, soft fades — trauma-safe, intimate
 * CTA: Train Your Seeing → $47 challenge
 */
import { useState, useEffect } from "react";

const MIRROR_TABLE = [
  ["Surface hidden assumptions", "Presuppositions panel — what every sentence assumes without saying"],
  ["Name what you sense", "Formal name — gives language to the pattern you already feel"],
  ["See structure in experience", "Seven-step decomposition of any sentence or situation"],
  ["Daily practice with discipline", "XP + streak system — discipline as infrastructure"],
  ["Root work", "Roots ℵ — literal linguistic root system, ancestral semantic architecture"],
  ["Pattern recognition", "Challenge + Sparring modes — train the eye before the reveal"],
];

const PATH = [
  ["01–07", "Presuppositions", "What does this sentence assume? Surface the hidden frame before the analysis. Learn to see what isn't said."],
  ["08–14", "Fallacy recognition", "Sparring mode L3-L4. Name the break before the reveal. The patterns that manipulate have names."],
  ["15–21", "Formal naming", "Challenge mode. Build the vocabulary that turns intuition into language you can use."],
  ["22–28", "Root architecture", "Roots ℵ. Map three-letter roots to logical primitives. Your ancestral naming stack."],
  ["29–30", "Full integration", "All modes. Your weak points surfaced, your pattern library built. The 30-day report."],
];

const DEMO_SENTENCE = "You're too sensitive — you're reading too much into what I said.";
const DEMO_PRESUPPOSITIONS = [
  "That the speaker's interpretation of the situation is the neutral or correct one",
  "That emotional response is a defect in perception rather than a valid signal",
  "That 'reading into' things is inherently error-prone rather than potentially accurate",
  "That the listener's internal state is the problem, not the content of what was said",
];

export default function MirrorBloom() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => setVisible(true), 80);
  }, []);

  const C = {
    bg: "#faf7f3",
    white: "#fff",
    text: "#1a1614",
    muted: "#7a7068",
    accent: "#8a5040",
    gold: "#a08030",
    border: "#e0d8d0",
    borderLight: "#ede8e2",
    soft: "#f4f0ea",
  };

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"Georgia,'Times New Roman',serif", opacity: visible ? 1 : 0, transition:"opacity .6s ease" }}>
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        .mono { font-family:'Courier New',monospace; }
        a { color:${C.text}; }
        .btn-rose { display:inline-block; padding:13px 32px; background:${C.accent}; color:#faf7f3; font-family:'Courier New',monospace; font-size:13px; letter-spacing:.06em; cursor:pointer; border:none; text-decoration:none; transition:all .25s; }
        .btn-rose:hover { background:#6a3830; }
        .btn-outline { display:inline-block; padding:12px 28px; background:transparent; color:${C.text}; font-family:'Courier New',monospace; font-size:12px; letter-spacing:.06em; cursor:pointer; border:1px solid ${C.border}; text-decoration:none; transition:all .25s; }
        .btn-outline:hover { border-color:${C.accent}; }
        .rule { height:1px; background:${C.border}; width:100%; }
        .bloom-fade { animation: bf .8s ease both; }
        @keyframes bf { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:none } }
        .s1 { animation-delay:.1s } .s2 { animation-delay:.25s } .s3 { animation-delay:.4s }
        .path-row { display:flex; gap:24px; padding:22px 0; border-bottom:1px solid ${C.borderLight}; align-items:flex-start; }
        .mirror-row { display:flex; gap:20px; padding:14px 0; border-bottom:1px solid ${C.borderLight}; }
        .presup-item { padding:12px 0; border-bottom:1px solid ${C.borderLight}; display:flex; gap:12px; font-size:15px; line-height:1.7; }
      `}</style>

      {/* Nav */}
      <nav style={{ borderBottom:`1px solid ${C.border}`, padding:"0 48px", height:52, display:"flex", alignItems:"center", justifyContent:"space-between", background:C.white }}>
        <div style={{ display:"flex", alignItems:"baseline", gap:10 }}>
          <span style={{ fontSize:16 }}>Pattern Glow™</span>
          <span className="mono" style={{ fontSize:10, color:C.muted, letterSpacing:".1em" }}>EMET ENGINE</span>
        </div>
        <div style={{ display:"flex", gap:28, alignItems:"center" }}>
          <a href="#path" className="mono" style={{ fontSize:11, color:C.muted, textDecoration:"none" }}>30-day path</a>
          <a href="#demo" className="mono" style={{ fontSize:11, color:C.muted, textDecoration:"none" }}>Try it</a>
          <a href="#pricing" className="mono" style={{ fontSize:11, color:C.muted, textDecoration:"none" }}>Pricing</a>
          <a href="/press" className="mono" style={{ fontSize:11, color:C.muted, textDecoration:"none" }}>Press</a>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding:"88px 48px 80px", maxWidth:960, margin:"0 auto", borderBottom:`1px solid ${C.border}` }}>
        <p className="mono bloom-fade s1" style={{ fontSize:10, color:C.muted, letterSpacing:".14em", marginBottom:20 }}>COGNITIVE PRECISION TRAINING · PERSONAL DEVELOPMENT</p>
        <h1 className="bloom-fade s1" style={{ fontSize:"clamp(32px,5vw,54px)", fontWeight:400, lineHeight:1.18, marginBottom:24, maxWidth:640 }}>
          You already do the work of seeing.<br />
          This gives the patterns names.
        </h1>
        <p className="bloom-fade s2" style={{ fontSize:18, lineHeight:1.8, color:C.muted, maxWidth:560, marginBottom:16, fontStyle:"italic" }}>
          Seven minutes a day. One sentence at a time. A vocabulary that grows with every session.
        </p>
        <p className="bloom-fade s2" style={{ fontSize:16, lineHeight:1.75, color:C.muted, maxWidth:560, marginBottom:40 }}>
          The patterns in language that affect your life have names. Pattern Glow™ trains you to find them — in what's said to you, in what you say to yourself, and in the documents that shape your world.
        </p>
        <div className="bloom-fade s3" style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
          <a href={"/checkout/pattern_glow_challenge"} className="btn-rose" target="_blank" rel="noopener noreferrer">Start the 30-day challenge — $47</a>
          <a href="#demo" className="btn-outline">See how it works</a>
        </div>
        <p className="mono bloom-fade s3" style={{ fontSize:11, color:C.muted, marginTop:16, letterSpacing:".04em" }}>
          Or upgrade to Scholar ($29/month) to include the full path + day tracking.
        </p>
      </section>

      {/* The connection */}
      <section style={{ padding:"72px 48px", maxWidth:960, margin:"0 auto", borderBottom:`1px solid ${C.border}` }}>
        <p className="mono" style={{ fontSize:10, color:C.muted, letterSpacing:".14em", marginBottom:20 }}>WHAT MIRROR BLOOM LOGIC DOES</p>
        <h2 style={{ fontSize:28, fontWeight:400, marginBottom:40 }}>Mirror work meets formal structure.</h2>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0, background:C.border }}>
          <div style={{ background:C.soft, padding:"14px 20px", borderBottom:`1px solid ${C.border}` }}>
            <span className="mono" style={{ fontSize:10, color:C.accent, letterSpacing:".08em" }}>MIRROR WORK</span>
          </div>
          <div style={{ background:C.soft, padding:"14px 20px", borderBottom:`1px solid ${C.border}` }}>
            <span className="mono" style={{ fontSize:10, color:C.muted, letterSpacing:".08em" }}>LOGIC RIGOR EQUIVALENT</span>
          </div>
          {MIRROR_TABLE.map(([left, right]) => (
            <>
              <div key={left} style={{ background:C.white, padding:"14px 20px", borderBottom:`1px solid ${C.borderLight}`, fontSize:14, color:C.text }}>
                {left}
              </div>
              <div key={right} style={{ background:C.white, padding:"14px 20px", borderBottom:`1px solid ${C.borderLight}`, fontSize:14, color:C.muted }}>
                {right}
              </div>
            </>
          ))}
        </div>
        <p style={{ marginTop:24, fontSize:15, color:C.muted, fontStyle:"italic" }}>
          Not therapy. Not diagnosis. Cognitive precision training — the discipline of naming what you already sense.
        </p>
      </section>

      {/* Demo */}
      <section id="demo" style={{ padding:"72px 48px", maxWidth:960, margin:"0 auto", borderBottom:`1px solid ${C.border}` }}>
        <p className="mono" style={{ fontSize:10, color:C.muted, letterSpacing:".14em", marginBottom:20 }}>TRY IT</p>
        <h2 style={{ fontSize:28, fontWeight:400, marginBottom:12 }}>The presuppositions panel.</h2>
        <p style={{ fontSize:16, color:C.muted, lineHeight:1.7, maxWidth:560, marginBottom:32 }}>
          Every sentence carries hidden assumptions. The Mirror Bloom entry point is learning to see them. This is the first thing you practice in the 30-day path.
        </p>

        <div style={{ background:C.white, border:`1px solid ${C.border}`, padding:"24px" }}>
          <p className="mono" style={{ fontSize:10, color:C.muted, letterSpacing:".1em", marginBottom:12 }}>SENTENCE</p>
          <p style={{ fontSize:17, fontStyle:"italic", color:C.text, marginBottom:20, lineHeight:1.65 }}>"{DEMO_SENTENCE}"</p>
          <div className="rule" style={{ marginBottom:20 }} />
          {!demoOpen ? (
            <button className="btn-outline" onClick={() => setDemoOpen(true)}>
              Reveal what this sentence assumes →
            </button>
          ) : (
            <div>
              <p className="mono" style={{ fontSize:10, color:C.accent, letterSpacing:".1em", marginBottom:16 }}>HIDDEN PRESUPPOSITIONS</p>
              {DEMO_PRESUPPOSITIONS.map((p, i) => (
                <div key={i} className="presup-item" style={{ animation:`bf .5s ease ${i * .1}s both` }}>
                  <span style={{ color:C.accent, minWidth:14, flexShrink:0, paddingTop:3 }}>—</span>
                  <span>{p}</span>
                </div>
              ))}
              <div style={{ marginTop:20, padding:"16px 20px", background:C.soft, borderLeft:`3px solid ${C.accent}` }}>
                <p style={{ fontSize:14, color:C.muted, lineHeight:1.7 }}>
                  The analysis also returns: the logical structure, failure modes, a formal name for the argument pattern, and a rewrite. The 30-day path trains you to see this in real time — in conversations, in documents, in your own thinking.
                </p>
              </div>
              <div style={{ marginTop:24 }}>
                <a href={"/checkout/pattern_glow_challenge"} className="btn-rose" target="_blank" rel="noopener noreferrer">
                  Start the 30-day path — $47
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 30-day path */}
      <section id="path" style={{ padding:"72px 48px", maxWidth:960, margin:"0 auto", borderBottom:`1px solid ${C.border}` }}>
        <p className="mono" style={{ fontSize:10, color:C.muted, letterSpacing:".14em", marginBottom:20 }}>THE 30-DAY PATH</p>
        <h2 style={{ fontSize:28, fontWeight:400, marginBottom:12 }}>One practice. Five phases.</h2>
        <p style={{ fontSize:16, color:C.muted, maxWidth:560, marginBottom:40 }}>
          Each phase builds the next. By day 30, you have a vocabulary, a ledger, and a cognitive precision report that shows you what you built.
        </p>
        {PATH.map(([days, title, body]) => (
          <div key={days} className="path-row">
            <div style={{ minWidth:72 }}>
              <span className="mono" style={{ fontSize:24, color:C.borderLight, fontWeight:500 }}>{days}</span>
            </div>
            <div>
              <div style={{ fontSize:18, marginBottom:8 }}>{title}</div>
              <div style={{ fontSize:14, lineHeight:1.7, color:C.muted }}>{body}</div>
            </div>
          </div>
        ))}
        <div style={{ marginTop:40, padding:"28px", background:C.white, border:`1px solid ${C.border}` }}>
          <p className="mono" style={{ fontSize:10, color:C.muted, letterSpacing:".1em", marginBottom:8 }}>DAY 30 OUTPUT</p>
          <p style={{ fontSize:16, marginBottom:8 }}>The Mirror Bloom Report</p>
          <p style={{ fontSize:14, color:C.muted, lineHeight:1.7 }}>
            After 30 days, the system generates a personal cognitive precision report: vocabulary acquired, fallacies learned, roots mapped, XP earned, error patterns surfaced. The report is yours to keep and share.
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ padding:"72px 48px", maxWidth:960, margin:"0 auto", borderBottom:`1px solid ${C.border}` }}>
        <p className="mono" style={{ fontSize:10, color:C.muted, letterSpacing:".14em", marginBottom:20 }}>PRICING</p>
        <h2 style={{ fontSize:28, fontWeight:400, marginBottom:40 }}>Start with the challenge. Stay with the practice.</h2>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:1, background:C.border }}>
          {[
            {
              name: "Free",
              price: "$0",
              period: "",
              features: ["10 analyses/day", "Solo + Challenge modes", "Vocabulary ledger"],
              cta: "Start free",
              href: "https://logic.naci.tech/app",
              primary: false,
            },
            {
              name: "30-Day Challenge",
              price: "$47",
              period: " one-time",
              features: ["Full 30-day path access", "Day tracking", "All 5 modes", "Mirror Bloom Report at day 30"],
              cta: "Start the challenge",
              href: "/checkout/pattern_glow_challenge",
              primary: true,
            },
            {
              name: "Scholar",
              price: "$29",
              period: "/month",
              features: ["Everything in challenge", "Unlimited analyses", "Root bank + naming stack", "API access", "Priority model"],
              cta: "Start Scholar",
              href: "/checkout/pattern_glow_scholar",
              primary: false,
            },
          ].map(tier => (
            <div key={tier.name} style={{ background: tier.primary ? C.soft : C.white, padding:"28px 24px" }}>
              <p className="mono" style={{ fontSize:10, color:C.muted, letterSpacing:".08em", marginBottom:12 }}>{tier.name.toUpperCase()}</p>
              <div style={{ display:"flex", alignItems:"baseline", gap:4, marginBottom:24 }}>
                <span style={{ fontSize:30, fontWeight:400 }}>{tier.price}</span>
                <span className="mono" style={{ fontSize:12, color:C.muted }}>{tier.period}</span>
              </div>
              <div style={{ marginBottom:24 }}>
                {tier.features.map(f => (
                  <div key={f} style={{ display:"flex", gap:10, padding:"6px 0", fontSize:13, color:C.muted, borderBottom:`1px solid ${C.borderLight}` }}>
                    <span style={{ color:C.accent }}>—</span>{f}
                  </div>
                ))}
              </div>
              <a href={tier.href} className={tier.primary ? "btn-rose" : "btn-outline"}
                style={{ display:"block", width:"100%", textAlign:"center", fontSize:12 }}
                target={tier.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer">
                {tier.cta}
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding:"80px 48px", textAlign:"center", maxWidth:640, margin:"0 auto" }}>
        <h2 style={{ fontSize:32, fontWeight:400, marginBottom:20, lineHeight:1.2 }}>
          Seven minutes a day.<br />One sentence at a time.
        </h2>
        <p style={{ fontSize:16, color:C.muted, lineHeight:1.75, marginBottom:36 }}>
          The patterns have names. The vocabulary accumulates. The practice compounds.
        </p>
        <a href={"/checkout/pattern_glow_challenge"} className="btn-rose" style={{ fontSize:14, padding:"14px 40px" }}
          target="_blank" rel="noopener noreferrer">
          Train Your Seeing →
        </a>
      </section>

      {/* Footer */}
      <footer style={{ padding:"28px 48px", borderTop:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:16 }}>
        <span className="mono" style={{ fontSize:10, color:C.muted, letterSpacing:".08em" }}>
          PATTERN GLOW™ / MIRROR BLOOM LOGIC — EMET ENGINE — AN ELLARI VENTURES SYSTEM — 2026
        </span>
        <div style={{ display:"flex", gap:24 }}>
          {[["Logic Rigor", "/"], ["Inspection Defense", "/inspection-defense"], ["Press", "/press"], ["Ellari Ventures", "https://ellari.dev"]].map(([l, h]) => (
            <a key={l} href={h} className="mono" style={{ fontSize:10, color:C.muted, textDecoration:"none" }}>{l}</a>
          ))}
        </div>
      </footer>
    </div>
  );
}
